import { useEffect, useRef, useState, type ReactNode } from 'react';

interface CollapsibleHeaderProps {
  collapsed: boolean;
  /** When false the children render untouched, whatever `collapsed` says. */
  enabled?: boolean;
  children: ReactNode;
}

/**
 * Collapses its content to zero height when `collapsed` is true.
 *
 * A location's hero is worth a screenful on arrival and in the way once the
 * climber is reading the climbs, so both profiles fold it away when a tab is
 * picked and bring it back on a deliberate pull.
 *
 * Uses the grid `1fr → 0fr` technique so auto-height content animates without a
 * fixed max-height. Overflow is only clipped during the animation and while
 * collapsed, so at rest the full-bleed hero can still extend past the content box.
 */
export function CollapsibleHeader({ collapsed, enabled = true, children }: CollapsibleHeaderProps) {
  const [animating, setAnimating] = useState(false);
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setAnimating(true);
    // Fallback in case the grid-template-rows transition never fires transitionend.
    const id = setTimeout(() => setAnimating(false), 400);
    return () => clearTimeout(id);
  }, [collapsed]);

  if (!enabled) return <>{children}</>;

  const clip = collapsed || animating;
  return (
    <div
      className="grid transition-[grid-template-rows] duration-300 ease-in-out"
      style={{ gridTemplateRows: collapsed ? '0fr' : '1fr' }}
      aria-hidden={collapsed}
    >
      <div className={clip ? 'min-h-0 overflow-hidden' : 'min-h-0'}>{children}</div>
    </div>
  );
}
